/**
 * @module
 * @author Bayu Dwiyan Satria
 * @since 1.0.0
 */

/**
 * Vue
 */
import { createI18n } from 'vue-i18n'

/**
 * Config
 */
import { siteConfig } from '@/config/site'

/**
 * Locales
 */
import en from '@/locales/en.json'
import id from '@/locales/id.json'

/** Key under which the visitor's chosen locale is persisted. */
const STORAGE_KEY = 'locale'

/**
 * Locales the application ships. Keys are BCP 47 tags and must match `siteConfig.language` and the
 * `alternateLocales` entries, minus the region suffix.
 */
const messages = { en, id }

/**
 * Read the visitor's preferred locale.
 *
 * @remarks
 * Returns the configured default during prerendering. The static HTML is generated once, so it can
 * only ever carry one language — the visitor's stored preference is applied after hydration.
 *
 * A site that genuinely needs indexable translations must prerender each locale under its own path
 * (`/`, `/id/`) and cross-link them with `hreflang`. Switching locale client-side is a UX feature,
 * not a discoverability one: search engines index the URL, and one URL cannot rank as two
 * languages. See `docs/guide/seo.md`.
 */
function detectLocale(): string {
  if (typeof window === 'undefined') {
    return siteConfig.language
  }

  const stored = window.localStorage.getItem(STORAGE_KEY)

  if (stored && stored in messages) {
    return stored
  }

  const browser = window.navigator.language.split('-')[0]

  return browser in messages ? browser : siteConfig.language
}

/**
 * The application's i18n instance.
 *
 * @remarks
 * `legacy: false` selects the Composition API mode, which is the only mode with usable TypeScript
 * inference. Missing-key warnings are silenced in production because a missing translation should
 * degrade to the key, not fill a visitor's console.
 */
export const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: detectLocale(),
  fallbackLocale: siteConfig.language,
  missingWarn: import.meta.env.DEV,
  fallbackWarn: import.meta.env.DEV,
  messages
})
